import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Activity, Target, Scale, ChevronRight, Ruler } from 'lucide-react';

const GOALS = ['Lose Weight', 'Maintain Weight', 'Gain Weight'];

export default function Onboarding() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [step, setStep] = useState(0);
  const [units, setUnits] = useState('imperial'); // 'imperial' | 'metric'
  const [form, setForm] = useState({
    name: '',
    goal: 'Lose Weight',
    startWeight: '',
    goalWeight: '',
    height: '',
    age: '',
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const wLabel = units === 'metric' ? 'kg' : 'lb';
  const wPlaceholder = units === 'metric' ? ['77', '68'] : ['170', '150'];
  const hPlaceholder = units === 'metric' ? '178' : "5'8\"";
  const hLabel = units === 'metric' ? 'cm' : 'ft/in';

  const finish = () => {
    const profile = {
      name: form.name.trim() || 'You',
      goal: form.goal,
      units,
      startWeight: parseFloat(form.startWeight) || (units === 'metric' ? 77 : 170),
      currentWeight: parseFloat(form.startWeight) || (units === 'metric' ? 77 : 170),
      goalWeight: parseFloat(form.goalWeight) || (units === 'metric' ? 68 : 150),
      height: form.height.trim() || (units === 'metric' ? '178 cm' : "5'8\""),
      age: parseInt(form.age) || 25,
      joinedDate: new Date().toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
    };
    setUser(profile);
    navigate('/dashboard', { replace: true });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0066ee] to-[#004bbf] flex flex-col">
      {step === 0 && (
        <div className="flex-1 flex flex-col items-center justify-center px-8 text-white text-center">
          <div className="w-20 h-20 bg-white/20 rounded-3xl flex items-center justify-center mb-6">
            <Activity size={40} className="text-white" />
          </div>
          <h1 className="text-4xl font-bold mb-3">Trackr</h1>
          <p className="text-blue-100 text-lg mb-10">Your personal fitness &amp; nutrition tracker. Free, private, works offline.</p>
          <button
            onClick={() => setStep(1)}
            className="w-full max-w-xs bg-white text-[#0066ee] font-bold py-4 rounded-2xl text-lg shadow-xl press-scale"
          >
            Get Started
          </button>
        </div>
      )}

      {step === 1 && (
        <div className="flex-1 flex flex-col px-6 pt-16 pb-8">
          <div className="text-white mb-8">
            <p className="text-blue-200 text-sm font-semibold uppercase tracking-wider mb-1">Step 1 of 3</p>
            <h2 className="text-2xl font-bold">About you</h2>
          </div>
          <div className="bg-white rounded-3xl p-6 flex-1 flex flex-col gap-5 shadow-xl overflow-y-auto">
            <Field label="Your name" placeholder="e.g. Alex" value={form.name} onChange={(v) => set('name', v)} />
            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Goal</label>
              <div className="flex flex-col gap-2">
                {GOALS.map((g) => (
                  <button
                    key={g}
                    onClick={() => set('goal', g)}
                    className={`flex items-center gap-3 px-4 py-3 rounded-xl border-2 transition-colors text-left font-semibold text-sm ${
                      form.goal === g
                        ? 'border-[#0066ee] bg-blue-50 text-[#0066ee]'
                        : 'border-gray-200 text-gray-700'
                    }`}
                  >
                    <Target size={16} />
                    {g}
                  </button>
                ))}
              </div>
            </div>
            <button
              onClick={() => setStep(2)}
              className="mt-auto flex items-center justify-center gap-2 bg-[#0066ee] text-white font-bold py-4 rounded-2xl shadow-md press-scale"
            >
              Continue <ChevronRight size={18} />
            </button>
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="flex-1 flex flex-col px-6 pt-16 pb-8">
          <div className="text-white mb-8">
            <p className="text-blue-200 text-sm font-semibold uppercase tracking-wider mb-1">Step 2 of 3</p>
            <h2 className="text-2xl font-bold">Units</h2>
          </div>
          <div className="bg-white rounded-3xl p-6 flex-1 flex flex-col gap-5 shadow-xl">
            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Measurement system</label>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { val: 'imperial', title: 'Imperial', sub: 'lb, ft/in' },
                  { val: 'metric', title: 'Metric', sub: 'kg, cm' },
                ].map(({ val, title, sub }) => (
                  <button
                    key={val}
                    onClick={() => setUnits(val)}
                    className={`flex flex-col items-center py-5 rounded-2xl border-2 transition-colors ${
                      units === val
                        ? 'border-[#0066ee] bg-blue-50'
                        : 'border-gray-200'
                    }`}
                  >
                    <Ruler size={24} className={units === val ? 'text-[#0066ee]' : 'text-gray-400'} />
                    <p className={`mt-2 font-bold text-sm ${units === val ? 'text-[#0066ee]' : 'text-gray-700'}`}>{title}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{sub}</p>
                  </button>
                ))}
              </div>
            </div>
            <button
              onClick={() => setStep(3)}
              className="mt-auto flex items-center justify-center gap-2 bg-[#0066ee] text-white font-bold py-4 rounded-2xl shadow-md press-scale"
            >
              Continue <ChevronRight size={18} />
            </button>
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="flex-1 flex flex-col px-6 pt-16 pb-8">
          <div className="text-white mb-8">
            <p className="text-blue-200 text-sm font-semibold uppercase tracking-wider mb-1">Step 3 of 3</p>
            <h2 className="text-2xl font-bold">Your stats</h2>
          </div>
          <div className="bg-white rounded-3xl p-6 flex-1 flex flex-col gap-5 shadow-xl">
            <div className="grid grid-cols-2 gap-4">
              <Field
                label={`Current weight (${wLabel})`}
                placeholder={wPlaceholder[0]}
                type="number"
                value={form.startWeight}
                onChange={(v) => set('startWeight', v)}
              />
              <Field
                label={`Goal weight (${wLabel})`}
                placeholder={wPlaceholder[1]}
                type="number"
                value={form.goalWeight}
                onChange={(v) => set('goalWeight', v)}
              />
              <Field
                label={`Height (${hLabel})`}
                placeholder={hPlaceholder}
                value={form.height}
                onChange={(v) => set('height', v)}
              />
              <Field
                label="Age"
                placeholder="25"
                type="number"
                value={form.age}
                onChange={(v) => set('age', v)}
              />
            </div>
            <div className="flex items-start gap-3 bg-blue-50 rounded-xl p-3 mt-1">
              <Scale size={18} className="text-[#0066ee] mt-0.5 flex-shrink-0" />
              <p className="text-xs text-blue-700">All data is stored locally on your device — nothing is sent to any server.</p>
            </div>
            <button
              onClick={finish}
              className="mt-auto flex items-center justify-center gap-2 bg-[#0066ee] text-white font-bold py-4 rounded-2xl shadow-md press-scale"
            >
              Start Tracking <ChevronRight size={18} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, placeholder, value, onChange, type = 'text' }) {
  return (
    <div>
      <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">{label}</label>
      <input
        type={type}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 text-sm font-semibold text-gray-900 outline-none focus:border-[#0066ee] transition-colors"
      />
    </div>
  );
}
